import type { StyleRuleLogic } from '../map-style-workbench/builderLogic';
import type { MapComponentFilterField } from '../map-style-workbench/types';

export type LayerRuleMode = 'hide_non_matching';

export type LayerRuleOpacityKind = 'fill' | 'line' | 'circle' | 'symbol';

export type LayerRuleId = string;

// Avoid inline hex literals to satisfy frontend lint rules in consuming apps.
export const DEFAULT_LAYER_RULE_COLOR = 'rgb(59 130 246)'; // Tailwind blue-500

export interface LayerRuleDefinition {
  id: LayerRuleId;
  enabled: boolean;
  label: string;
  color: string;
  logic: StyleRuleLogic;
  representation?: 'fill' | 'line' | 'circle' | 'symbol' | 'fill-extrusion' | null;
  paint?: Record<string, unknown> | null;
  layout?: Record<string, unknown> | null;
}

export interface LayerRuleLayerState {
  sourceId: string;
  sourceAlias: string;
  sourceLayer: string;
  mode: LayerRuleMode;
  // Field list used by the UI. Primary source is PMTiles vector-layer metadata;
  // callers may augment it (sampling, manual fields).
  fields: MapComponentFilterField[];
  rules: LayerRuleDefinition[];
}

export interface LayerRuleBuilderState {
  version: 1;
  layers: LayerRuleLayerState[];
}

// Persisted metadata stored on map_style.metadata.rule_builder (v1).
// This is intentionally a JSON-friendly shape (snake_case) to align with docs.
export interface LayerRuleBuilderMetadataV1 {
  version: 1;
  sources: Array<{ source_id: string; alias: string }>;
  layers: Array<{
    source_id: string;
    source_alias: string;
    source_layer: string;
    mode: LayerRuleMode;
    rules: Array<{
      id: string;
      enabled: boolean;
      label: string;
      color: string;
      representation?: LayerRuleDefinition['representation'];
      paint?: Record<string, unknown> | null;
      layout?: Record<string, unknown> | null;
      predicate: PersistedLogic;
    }>;
  }>;
}

type PersistedLogic = PersistedGroup | PersistedCondition;

type PersistedGroup = {
  type: 'group';
  operator: 'AND' | 'OR';
  children: PersistedLogic[];
};

type PersistedCondition = {
  type: 'condition';
  field_id: string;
  operator_id: string;
  values: string[];
};
